import { useEffect, useMemo, useState } from "react";
import TopNav from "../components/TopNav";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ImportContacts } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import { APICustomer, getAllCustomers, saveCustomer } from "../api/customers";
import axios from "axios";
import pageStyle from "./ContactForm.module.css";

type Lead = {
  id: number | string;
  firstname: string;
  lastname: string;
  address: string;
  city: string;
  zipcode: string;
  state: string;
  phone: string;
  email: string;
  voucher: string;
  message: string;
};

const digits = (s?: string) => (s || "").replace(/\D/g, "").slice(-10);

function ContactFormImportPage() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 900px)");
  const [selected, setSelected] = useState<readonly GridRowId[]>([]);
  const [rows, setRows] = useState<Lead[]>([]);
  const [customers, setCustomers] = useState<APICustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get("https://dashboard.lifetreewny.com/api/dashboard/formData/", {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      .then((d) => setRows(d.data))
      .catch(() =>
        setError(
          "Couldn't load contact form submissions from the dashboard. Check the connection and try again."
        )
      )
      .finally(() => setLoading(false));
    getAllCustomers().then(setCustomers);
  }, []);

  // Leads whose email or phone already belongs to a customer.
  const existing = useMemo(() => {
    const emails = new Set(
      customers.map((c) => c.email.trim().toLowerCase()).filter(Boolean)
    );
    const phones = new Set(customers.map((c) => digits(c.phone)).filter(Boolean));
    return new Set(
      rows
        .filter(
          (r) =>
            (r.email && emails.has(r.email.trim().toLowerCase())) ||
            (digits(r.phone) && phones.has(digits(r.phone)))
        )
        .map((r) => r.id)
    );
  }, [rows, customers]);

  const toggle = (id: GridRowId) =>
    setSelected(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    );

  const handleImport = async () => {
    setImporting(true);
    let count = 0;
    for (const sid of selected) {
      const row = rows.find((v) => v.id === sid);
      if (row === undefined) {
        continue;
      }
      const customer: APICustomer = {
        firstName: row.firstname || "",
        lastName: row.lastname || "",
        addressLine1: row.address || "",
        addressLine2: "",
        city: row.city || "",
        zip: row.zipcode || "",
        state: row.state || "NY",
        phone: row.phone || "",
        phoneExt: "",
        email: row.email || "",
        notes: [row.message, row.voucher && `Voucher: ${row.voucher}`]
          .filter(Boolean)
          .join("\n"),
      };
      await saveCustomer(customer);
      count++;
    }
    setImporting(false);
    window.alert(`Imported ${count} customer${count === 1 ? "" : "s"}.`);
    navigate("/customers");
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 160,
      valueGetter: (_v, r: Lead) => `${r.firstname} ${r.lastname}`.trim(),
    },
    { field: "phone", headerName: "Phone", minWidth: 130 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, r: Lead) =>
        [r.address, r.city, `${r.state || ""} ${r.zipcode || ""}`.trim()]
          .filter(Boolean)
          .join(", "),
    },
    { field: "voucher", headerName: "Voucher", minWidth: 110 },
    { field: "message", headerName: "Message", flex: 1.5, minWidth: 240 },
    {
      field: "existing",
      headerName: "",
      minWidth: 150,
      sortable: false,
      renderCell: (p) =>
        existing.has(p.row.id) ? (
          <Chip size="small" label="Already a customer" />
        ) : null,
    },
  ];

  return (
    <div className="page">
      <TopNav pageTitle="Contact Form" />
      <main className={pageStyle.main}>
        <div className={pageStyle.header}>
          <div>
            <Typography variant="h6">Website leads</Typography>
            <Typography variant="body2" color="text.secondary">
              Select the people to add as customers.
            </Typography>
          </div>
          <Button
            onClick={handleImport}
            variant="contained"
            startIcon={<ImportContacts />}
            disabled={selected.length === 0 || importing}
          >
            {importing
              ? "Importing…"
              : `Import ${selected.length || ""} Selected`.replace("  ", " ")}
          </Button>
        </div>

        {error && <Alert severity="error">{error}</Alert>}
        {loading && (
          <div className={pageStyle.loading}>
            <CircularProgress />
          </div>
        )}

        {!loading &&
          !error &&
          (isDesktop ? (
            <div className={pageStyle.gridBox}>
              <DataGrid
                columns={columns}
                rows={rows}
                pagination
                checkboxSelection
                rowSelectionModel={[...selected]}
                onRowSelectionModelChange={setSelected}
                getRowHeight={() => "auto"}
                sx={{ background: "#fff" }}
              />
            </div>
          ) : (
            <div className={pageStyle.cards}>
              {rows.length === 0 && (
                <Typography color="text.secondary">No submissions.</Typography>
              )}
              {rows.map((r) => (
                <label key={r.id} className={pageStyle.card}>
                  <Checkbox
                    checked={selected.includes(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                  <div className={pageStyle.cardBody}>
                    <div className={pageStyle.cardTitle}>
                      <strong>
                        {`${r.firstname} ${r.lastname}`.trim() || "No name"}
                      </strong>
                      {existing.has(r.id) && (
                        <Chip size="small" label="Already a customer" />
                      )}
                    </div>
                    {r.phone && <div>{r.phone}</div>}
                    {r.email && <div>{r.email}</div>}
                    {r.address && (
                      <div className={pageStyle.muted}>
                        {[r.address, r.city].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {r.voucher && (
                      <div className={pageStyle.muted}>Voucher: {r.voucher}</div>
                    )}
                    {r.message && <p className={pageStyle.message}>{r.message}</p>}
                  </div>
                </label>
              ))}
            </div>
          ))}
      </main>
    </div>
  );
}

export default ContactFormImportPage;
